import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useMutation } from '@tanstack/react-query';
import { slidesService } from '../../services/slidesService';

interface SlidePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  html: string;
  markdown: string;
  lessonId: string;
  versionNumber: number;
  onMarkdownUpdated?: (markdown: string, html: string) => void;
}

export const SlidePreviewDialog: React.FC<SlidePreviewDialogProps> = ({
  open,
  onClose,
  html,
  markdown,
  lessonId,
  versionNumber,
  onMarkdownUpdated,
}) => {
  const [editingSlide, setEditingSlide] = useState<number | null>(null);
  const [instruction, setInstruction] = useState('');

  const { mutate: regenerate, isPending: isRegenerating } = useMutation({
    mutationFn: ({ slideIndex, instr }: { slideIndex: number; instr: string }) =>
      slidesService.regenerateSlide(lessonId, versionNumber, markdown, slideIndex, instr),
    onSuccess: (data) => {
      onMarkdownUpdated?.(data.markdown, data.html);
      setEditingSlide(null);
      setInstruction('');
    },
  });

  const handleRegenerate = () => {
    if (editingSlide !== null) {
      regenerate({ slideIndex: editingSlide, instr: instruction });
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Slide Preview
          </Typography>

          {onMarkdownUpdated && editingSlide === null && (
            <Tooltip title="Edit a slide (enter slide number)">
              <IconButton
                size="small"
                onClick={() => setEditingSlide(0)}
                sx={{ mr: 1 }}
                aria-label="Edit slide"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {editingSlide !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <TextField
                size="small"
                label="Slide #"
                type="number"
                value={editingSlide + 1}
                onChange={(e) => setEditingSlide(Math.max(0, Number(e.target.value) - 1))}
                sx={{ width: 80 }}
                inputProps={{ min: 1 }}
              />
              <TextField
                size="small"
                label="Instruction (optional)"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                sx={{ width: 260 }}
                placeholder="e.g. make the bullet points shorter"
              />
              <Tooltip title="Regenerate this slide">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                    color="primary"
                    aria-label="Regenerate slide"
                  >
                    {isRegenerating ? (
                      <CircularProgress size={18} />
                    ) : (
                      <AutorenewIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size="small" onClick={() => setEditingSlide(null)} aria-label="Cancel edit">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <IconButton onClick={onClose} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, position: 'relative', height: 'calc(100vh - 48px)' }}>
        {isRegenerating && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <iframe
          srcDoc={html}
          title="Slide Preview"
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </Box>
    </Dialog>
  );
};
